import { nutrientColors } from "@omnia/shared/nutrition/colors";
import { defineDirective } from "../../../omnia"
import { IFieldsService, INtrBalance, INtrReq, INutrient, INutritionService, NutrientGroup } from "../../../types"
import { DashboardSelection } from "../selection"
import { IDashboardContext, INutritionZone, INutritionZones } from "../types"
import { colorToCss, getWhiteBlackForegroundFromBackgroundColor } from "@omnia/shared/color/color";

export class ZonesCtrl {

    static create(): ng.IDirective {
        return defineDirective(ZonesCtrl, "app/nutrition/dashboard/map/zones/zones.html", {
            context: "="
        });
    }

    public context: IDashboardContext;

    public zones: INutritionZones;
    public visibleZones: INutritionZone[];

    static $inject = ["$scope", "OmniaNutritionService", "OmniaFieldsService"]

    constructor($scope: ng.IScope, private svc: INutritionService, private fieldSvc: IFieldsService) {

        $scope.$watch("vm.context", () => this.setupComponent());
        $scope.$watch("vm.selection", () => this.applySelection());
        $scope.$watch("vm.nutrient", () => this.applyNutrientFilter());

    }

    get selection(): DashboardSelection {
        if (this.context) {
            return this.context.selection;
        }
    }

    set selection(value: DashboardSelection) {
        this.context.setSelection(value, "ZonesCtrl/setter");
    }

    get nutrient(): INutrient {
        if (this.context) {
            return this.context.nutrients.nutrient
        }
    }

    protected setupComponent() {

        let ctxt = this.context;

        if (!ctxt) {
            return;
        }

        this.applySelection();

    }

    selectZone(zone: INutritionZone) {

        if (zone && this.context) {
            let s = this.context.selection.selectZone(zone.zoneNumber);
            this.context.setSelection(s, "ZonesCtrl/selectZone");
            this.context.mapCtxt.selectZoneOnCurrentView(s.croppingId, zone.zoneNumber);
        }

    }

    isZoneSelected(zone: INutritionZone): boolean {

        if (zone && this.context) {
            return zone.zoneNumber == this.context.selection.zoneNumber;
        }

    }

    getZoneStyle(zone: INutritionZone) {

        if (this.isZoneSelected(zone)) {

            let nutrientKey = this.context.nutrients.nutrient.key;
            let colors = nutrientColors[nutrientKey];

            if (colors) {

                let white = getWhiteBlackForegroundFromBackgroundColor(colors.baseColor);

                return {
                    "background-color": colorToCss(colors.baseColor),
                    "color": white ? "white" : "black"
                }
            }
        }

    }

    getAppZoneRate(zone: INutritionZone) {

        let nutrient = this.nutrient;

        if (this.context && this.context.settings.applications.showNutrientRates && nutrient) {
            return zone.nutrientRates[nutrient.key];
        }

        return zone.productRate;
    }

    private applySelection() {

        if (!this.context) return

        this.zones = this.getZonesForSelection()
        this.applyNutrientFilter()

    }


    private applyNutrientFilter() {

        let zones = this.zones ? this.zones.zones : null
        const n = this.nutrient

        if (!zones) {
            this.visibleZones = null
            return
        }

        if (n) {

            const group = Math.pow(2, n.nutrient - 1);

            zones = zones
                .filter(z => (z.nutrientGroup & group))
                .filter(z => (!z.nutrient) || (z.nutrient == n.nutrient))

        }

        zones = zones.filter(z => z.area >= 0.1)

        this.visibleZones = zones

    }

    public getZonesForSelection(): INutritionZones {

        let zones: INutritionZones;
        let s = this.selection;

        if (s) {

            if (s.croppingId && s.planId && s.appId) {
                zones = this.getApplicationZones(s.croppingId, s.planId, s.appId);
            }

            if (s.croppingId && s.planId && (!s.appId)) {
                zones = this.getPlanZones(s.croppingId, s.planId, !s.closing);
            }

            if (s.croppingId && (!s.planId) && (!s.appId)) {
                zones = this.getCroppingRequirementZones(s.croppingId, s.closing);
            }

        }

        return zones;

    }

    private getPlanZones(croppingId: number, planId: number, closing: boolean): INutritionZones {

        let provider = this.svc.getRequirementsProvider();
        let cropping = this.fieldSvc.findCropping(croppingId);
        let plan = this.context.planReader.getCroppingPlan(croppingId, planId);
        let requirements: INtrReq[];

        if (closing) {
            return null;
        }

        requirements = provider.getPlanRequirements(croppingId, planId);

        let zones = getRequirementZones(requirements);

        return <INutritionZones>{
            description: `Opening requirement zones for ${cropping.description} before plan ${plan.name}`,
            zones: zones,
            requirements: true,
            units: null
        }

    }

    public getApplicationZones(croppingId: number, planId: number, appId: number): INutritionZones {

        let plan = this.context.planReader.getCroppingPlan(croppingId, planId);
        if (!plan) return null;

        let app = plan.applications.find(a => a.appId == appId);
        if (!app) return null;
        if (!app.zones.length) return null;

        let zones = app.zones.map(z => {

            return {
                nutrientGroup: NutrientGroup.AllNutrients,
                area: Math.round(z.Area / 1000) / 10,
                zoneNumber: z.ZoneNumber,
                productRate: Math.round(z.ProductRate),
                nutrientRates: <any>z
            };

        }).sort((z1, z2) => z2.area - z1.area);

        return {
            description: `Application zones of ${app.stockName}`,
            zones,
            requirements: false,
            units: app.units
        }

    }

    private getCroppingRequirementZones(croppingId: number, closing: number): INutritionZones {

        const { svc, fieldSvc } = this

        const provider = svc.getRequirementsProvider();
        const cropping = fieldSvc.findCropping(croppingId);

        let requirements: INtrReq[]
        let descr: string

        switch (closing) {

            case 1:
                requirements = provider.getClosingRequirements(croppingId);
                descr = "Closing requirement"
                break;

            case -1:
                const { balance } = svc
                requirements = convertBalanceToRequirements(balance.getZones(croppingId))
                descr = "Opening balance"
                break;

            default:
                requirements = provider.getInitialRequirements(croppingId);
                descr = "Initial requirement"
                break;


        }

        let zones = getRequirementZones(requirements);

        return <INutritionZones>{
            description: `${descr} zones for ${cropping.description}`,
            zones,
            requirements: true,
            units: null
        }

    }

    showAppZoneEditDialog() {
        return this.context.editApplicationZones();
    }

    getRequirementUnits(): string {
        return (this.nutrient && this.nutrient.tonnesPerHa ? "t/ha" : "kg/ha");
    }

}

function getRequirementZones(requirements: INtrReq[]): INutritionZone[] {

    requirements = requirements.filter(r => r.Zone);
    if (!requirements.length) return null;

    let zones = requirements.map(z => {

        return {
            nutrientGroup: z.NutrientGroup,
            nutrient: z.Nutrient,
            area: Math.round(z.Area / 1000) / 10,
            zoneNumber: z.ZoneNumber,
            productRate: null,
            nutrientRates: {
                N: z.RequiredN,
                SO3: z.RequiredSO3,
                P2O5: z.RequiredP2O5,
                K2O: z.RequiredK2O,
                MgO: z.RequiredMgO,
                Lime: z.RequiredLime
            }
        };

    }).sort((z1, z2) => z2.area - z1.area);

    return zones;

}

function convertBalanceToRequirements(ob: INtrBalance[]): INtrReq[] {

    return (ob || []).map(b => <INtrReq><any>({
        Zone: b.Zone,
        ZoneNumber: b.ZoneNumber,
        NutrientGroup: b.NutrientGroup,
        Area: b.Area,
        RequiredN: b.N,
        RequiredP2O5: b.P2O5,
        RequiredK2O: b.K2O,
        RequiredMgO: b.MgO,
        RequiredLime: b.Lime
    }))

}
